import { z } from "zod";
import { Link } from "yarrr";
import React, { useState, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useHotkeys } from "react-hotkeys-hook";
import { Trash2 } from "lucide-react";

import useForm from "../../../Hooks/useForm";
import request from "../../../Api/request";
import Grid from "../../../Components/Grid";
import Modal from "../../../Components/Modal";
import Spacer from "../../../Components/Spacer";
import { Text } from "../../../Components/Text";
import Button from "../../../Components/Button";
import TextInput from "../../../Components/Inputs/Text";
import Textarea from "../../../Components/Inputs/Textarea";
import Table from "../../../Components/TableLite/TableLite";
import { Hotkey } from "../../../Components/Hotkey";

import css from "./styles.styl";

const DecisionLog = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(null);
	const { state, updateForm, resetForm } = useForm({
		schema: {
			name: z.string().min(1),
			description: z.string().min(1),
		},
		defaultValues: {
			name: "",
			description: "",
		},
	});

	useHotkeys("c", (event) => {
		event.preventDefault();
		setIsModalOpen(true);
	});

	const { data: projects, refetch: refetchProjects } = useQuery({
		queryKey: ["decision-log-projects"],
		queryFn: () => {
			return request({
				url: "/api/decision-log/projects",
				method: "GET",
			});
		},
	});

	const { mutate: createProject } = useMutation({
		mutationFn: async () => {
			setIsModalOpen(false);
			resetForm();

			const result = await request({
				url: "/api/decision-log/projects",
				method: "POST",
				data: {
					name: state.name,
					description: state.description,
				},
			});

			return result;
		},
		onSuccess: () => {
			refetchProjects();
		},
	});

	const { mutate: deleteProject } = useMutation({
		mutationFn: async (projectId) => {
			await request({
				url: `/api/decision-log/projects/${projectId}`,
				method: "DELETE",
			});
		},
		onSuccess: () => {
			refetchProjects();
			setConfirmDeleteModalOpen(null);
		},
	});

	const modalContent = (
		<>
			<div>
				<Text size={2} weight="medium" as="label">
					Decision Project Name
				</Text>
				<TextInput value={state.name} onChange={(value) => updateForm({ name: value })} />
			</div>

			<Spacer size={2} />

			<div>
				<Text size={2} weight="medium" as="label">
					Decision Project Description
				</Text>
				<Textarea value={state.description} onChange={(value) => updateForm({ description: value })} />
			</div>
		</>
	);

	const modalButtons = (
		<div className={css.modalButtons}>
			<Grid columns={["1fr"]}>
				<div className={css.modalButton}>
					<Button version={2} color="green" onClick={() => createProject()}>
						Create
					</Button>
				</div>
			</Grid>
		</div>
	);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const COLUMNS = [
		{
			id: "name",
			label: "Name",
			isSortable: false,
			getter: (row) => {
				return <Link href={`/decision-log/projects/${row._id}`}>{row.name}</Link>;
			},
		},
		{
			id: "description",
			label: "Description",
			isSortable: false,
		},
		{
			id: "actions",
			label: "",
			isSortable: false,
			width: "50px",
			getter: (row) => {
				return (
					<div>
						<Button
							className={css.deleteButton}
							size="small"
							color="gray"
							onClick={() => setConfirmDeleteModalOpen(row._id)}
						>
							<Trash2 size={16} />
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div className={css.container}>
			<Spacer size={2} />

			{isModalOpen && (
				<Modal size="large" content={modalContent} buttons={modalButtons} handleClose={handleCloseModal} />
			)}

			{confirmDeleteModalOpen ? (
				<Modal
					content="Are you sure you want to delete this project?"
					buttons={
						<div style={{ margin: "0 16px" }}>
							<Grid columns={["1fr", "1fr"]}>
								<Button color="red" onClick={() => deleteProject(confirmDeleteModalOpen)}>
									Yes, Delete
								</Button>
								<Button onClick={() => setConfirmDeleteModalOpen(false)}>Cancel</Button>
							</Grid>
						</div>
					}
					handleClose={() => setConfirmDeleteModalOpen(false)}
				/>
			) : null}

			<Grid columns={["1fr", "100px"]}>
				<div />
				<Button onClick={() => setIsModalOpen(true)}>
					<Hotkey>C</Hotkey>reate Project
				</Button>
			</Grid>

			<Spacer size={2} />

			<div className={css.gridContainer}>
				<Table columns={COLUMNS} rows={projects?.data || []} />
			</div>
		</div>
	);
};

export default DecisionLog;
